import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { COMPANY_INFO } from '../data/company';
import { ServiceIllustration } from '../components/ServiceIllustration';
import { WebForm } from '../components/WebForm';
import { SubpageHero } from '../components/SubpageHero';

const SERVICES_DATA = {
  'pillefyr': {
    title: "Pillefyr i Holsted, Vejen og Esbjerg - Få et tilbud i dag",
    metaTitle: "Pillefyr i Holsted, Vejen og Esbjerg - Få et tilbud i dag",
    metaDescription: "Leder du efter et pillefyr i Holsted? +40 års erfaring som certificerede montører i Vejen og Esbjerg. Kontakt os i dag",
    heading: "Pillefyr til helårshuse og erhverv med behov for stabil varme",
    subheading: "Få dimensionering, montering og service samlet ét sted",
    illustrationKey: "pillefyr",
    image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b7fb5f15-602f-4c3f-8819-ccd847432aa9/10bcdae0-e175-4f6a-964f-acf66250d46b-scaled.jpeg",
    content: `Ønsker du at få installeret et pillefyr i din bolig eller virksomhed? Et pillefyr vælges typisk, når du vil udskifte et ældre varmeanlæg og have en løsning dimensioneret præcist til bygningens varmebehov. Hos JNO Montage arbejder vi som energi montør med dimensionering, montering og service af pillefyr i Holsted tæt på Vejen samt i Esbjerg og nærliggende områder i Syd- og Vestjylland.

Vi installerer både træpillefyr og stokerfyr til private helårshuse, værksteder og kontorbygninger. Som energi montør vurderer vi altid bygningens eksisterende installation, før vi anbefaler en konkret løsning. Det gælder både ved udskiftning af ældre brændefyr og ved etablering af et helt nyt anlæg. I nogle tilfælde kan en luft til vand varmepumpe eller luft til luft varmepumpe være et alternativ, og vi rådgiver om forskellen, så du vælger den løsning, der passer bedst til bygningen.

Når vi installerer et pillefyr, dimensionerer vi anlægget ud fra varmebehov og eksisterende varmesystem. Det giver dig mulighed for at samle både montering og fremtidig service ét sted.`,
    advantages: [
      "Over 40 års erfaring med installation af pillefyr og varmeanlæg",
      "Korrekt dimensionering før valg af træpillefyr eller stokerfyr",
      "Montering og efterfølgende service samlet hos samme leverandør",
      "Installation i både private boliger og erhvervsbygninger",
      "Samlet tilbud med gennemsigtighed i pris og omfang"
    ],
    steps: [
      {
        step: "Trin 1: Gennemgang af eksisterende varmeanlæg",
        desc: "Vi gennemgår husets eller virksomhedens nuværende varmesystem og varmebehov. Det gælder både ved udskiftning af brændefyr, pilleovn eller andet ældre anlæg."
      },
      {
        step: "Trin 2: Dimensionering af pillefyr",
        desc: "Anlægget dimensioneres efter bygningens størrelse og forbrug. Valget mellem træpillefyr og stokerfyr afhænger af behov og installation."
      },
      {
        step: "Trin 3: Tilbud med samlet pris",
        desc: "Du modtager et samlet tilbud på pillefyr, montering og eventuel tilpasning af eksisterende installation. Det giver overblik over pris og arbejdets omfang."
      },
      {
        step: "Trin 4: Montering og tilslutning",
        desc: "Vi installerer pillefyret og tilslutter det til det eksisterende varmesystem. Efter montering gennemgår vi anlægget og dets funktion."
      },
      {
        step: "Trin 5: Service og vedligehold",
        desc: "Der kan aftales løbende service af pillefyr, så anlægget vedligeholdes korrekt. Det giver overblik over fremtidige priser og planlagt vedligehold."
      }
    ],
    relatedSlugs: ['luft-til-vand-varmepumpe', 'luft-til-luft-varmepumpe', 'varmepumpe-service']
  },

  'luft-til-luft-varmepumpe': {
    title: "Luft til luft varmepumpe i Holsted, Vejen og Esbjerg - Få et tilbud",
    metaTitle: "Luft til luft varmepumpe i Holsted, Vejen og Esbjerg - Få et tilbud",
    metaDescription: "Leder du efter en luft til luft varmepumpe i Holsted? +40 års erfaring som certificerede montører i Vejen og Esbjerg. Kontakt os i dag",
    heading: "Luft til luft varmepumpe installeret og serviceret samme sted",
    subheading: "Mere end 40 års erfaring med varmepumper",
    illustrationKey: "luft-til-luft",
    image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b7fb5f15-602f-4c3f-8819-ccd847432aa9/0b586e54-2cc8-4a6e-b4fb-35348a64d42e-scaled.jpeg",
    content: `Overvejer du at få installeret en luft til luft varmepumpe i din bolig eller virksomhed? En luft til luft varmepumpe kan bruges som primær varmekilde eller som supplement til eksisterende opvarmning, afhængigt af bygningens størrelse og behov. Hos JNO Montage arbejder vi som energi montør med installation af varmepumpe for kunder i Holsted tæt på Vejen samt i Esbjerg og nærliggende områder i Syd- og Vestjylland. I boliger hvor et pillefyr allerede er installeret, kan en luft til luft varmepumpe fungere som supplement i overgangsperioder.

Når vi installerer en luft til luft varmepumpe, starter vi med at vurdere rummenes størrelse, isoleringsforhold og placering af indedel og udedel. Det sikrer, at anlægget dimensioneres korrekt fra begyndelsen. Vi håndterer både montering af varmepumpe og service på varmepumpe, så du har én kontakt gennem hele forløbet.

Vi dimensionerer hver luft til luft varmepumpe efter rummenes størrelse og brug, så løsningen passer til både bolig og erhverv. Med over 40 års erfaring i energimontage har vi installeret og serviceret anlæg i mange forskellige bygningstyper.`,
    advantages: [
      "Over 40 års erfaring med varmepumper og energimontage",
      "Dimensionering baseret på boligens størrelse og behov",
      "Montering af varmepumpe udført af certificerede montører",
      "Service på varmepumpe kan planlægges ved installation",
      "Samlet tilbud med gennemsigtighed i pris og omfang"
    ],
    steps: [
      {
        step: "Trin 1: Gennemgang af bolig og varmebehov",
        desc: "Vi vurderer rummenes størrelse, isolering og anvendelse. Det danner grundlag for valg af den rette luft til luft varmepumpe."
      },
      {
        step: "Trin 2: Valg og dimensionering af anlæg",
        desc: "Modellen dimensioneres, så den passer til boligens faktiske behov. Placering af inde- og udedel planlægges for optimal varmefordeling."
      },
      {
        step: "Trin 3: Tilbud med samlet pris",
        desc: "Du modtager et samlet tilbud på luft til luft varmepumpe, installation og eventuel service. Det giver overblik over priser og arbejdets omfang."
      },
      {
        step: "Trin 4: Montering og tilslutning",
        desc: "Vi udfører installation af varmepumpe og sørger for korrekt tilslutning og indstilling af anlægget."
      },
      {
        step: "Trin 5: Test og plan for service",
        desc: "Anlægget testes og gennemgås, så det er klar til drift. Der kan aftales fremtidig service på varmepumpe for løbende vedligehold."
      }
    ],
    relatedSlugs: ['pillefyr', 'luft-til-vand-varmepumpe', 'varmepumpe-service']
  },

  'luft-til-vand-varmepumpe': {
    title: "Luft til vand varmepumpe i Holsted, Vejen og Esbjerg - Få et tilbud",
    metaTitle: "Luft til vand varmepumpe i Holsted, Vejen og Esbjerg - Få et tilbud",
    metaDescription: "Leder du efter en luft til vand varmepumpe i Holsted? +40 års erfaring som certificerede montører i Vejen og Esbjerg. Kontakt os i dag",
    heading: "Luft til vand varmepumpe med fokus på stabil helårsdrift",
    subheading: "Over 40 års erfaring med større varmeanlæg",
    illustrationKey: "luft-til-vand",
    image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b7fb5f15-602f-4c3f-8819-ccd847432aa9/luft-til-vand.jpg",
    content: `Står du foran at skulle installere en luft til vand varmepumpe som primær varmekilde i din bolig eller virksomhed? En luft til vand varmepumpe er udviklet til at levere varme til radiatorer og gulvvarme samt producere varmt brugsvand året rundt. Hos JNO Montage arbejder vi som energi montør med installation af varmepumpe i Holsted tæt på Vejen samt i Esbjerg og nærliggende områder i Syd- og Vestjylland.

Når vi installerer en luft til vand varmepumpe, tager vi udgangspunkt i bygningens samlede varmebehov. Det gælder både enfamiliehuse, større boliger, værksteder og kontorbygninger. Korrekt dimensionering er afgørende, når en luft til vand varmepumpe skal levere varme til hele det vandbårne system gennem vinteren. Løsningen kan også kombineres med solceller, hvis du ønsker at reducere elforbruget til opvarmning.

Vi håndterer både dimensionering, montering af varmepumpe og service på varmepumpe. Det betyder, at du får én samlet løsning, hvor installation og vedligehold tænkes sammen fra start.`,
    advantages: [
      "Over 40 års erfaring med større varmeanlæg",
      "Dimensionering baseret på husets varmeforbrug og eksisterende system",
      "Installation af varmepumpe udført af certificerede montører",
      "Service på varmepumpe kan aftales i samme forløb",
      "Samlet tilbud med gennemsigtighed i pris og montering"
    ],
    steps: [
      {
        step: "Trin 1: Gennemgang af eksisterende varmesystem",
        desc: "Vi vurderer husets eller bygningens nuværende centralvarme, radiatorer eller gulvvarme. Det gælder både ved udskiftning af olie, trækedel, pillefyr eller gasfyr."
      },
      {
        step: "Trin 2: Dimensionering og valg af model",
        desc: "Luft til vand varmepumpe dimensioneres ud fra bygningens varmeforbrug og kapacitet. Valg af model afhænger af boligens størrelse og behov for varmt brugsvand."
      },
      {
        step: "Trin 3: Tilbud med samlet pris",
        desc: "Du modtager et samlet tilbud på luft til vand varmepumpe, installation og eventuel tilpasning af eksisterende system."
      },
      {
        step: "Trin 4: Installation og tilslutning til centralvarme",
        desc: "Vi installerer varmepumpen og tilslutter den korrekt til det vandbårne varmesystem. Anlægget indreguleres, så det passer til radiatorer eller gulvvarme."
      },
      {
        step: "Trin 5: Test og plan for service",
        desc: "Anlægget testes og gennemgås, så det er klar til drift. Der kan aftales løbende service på varmepumpe for at sikre stabil helårsdrift."
      }
    ],
    relatedSlugs: ['solceller', 'luft-til-luft-varmepumpe', 'varmepumpe-service']
  },

  'solceller': {
    title: "Solceller i Holsted, Vejen og Esbjerg - Få et tilbud",
    metaTitle: "Solceller i Holsted, Vejen og Esbjerg - Få et tilbud",
    metaDescription: "Leder du efter solceller i Holsted? +40 års erfaring som certificerede montører i Vejen og Esbjerg. Kontakt os i dag",
    heading: "Solceller med dimensionering tilpasset din ejendom",
    subheading: "Installation af solcelleanlæg udført af erfarne montører",
    illustrationKey: "solceller",
    image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b7fb5f15-602f-4c3f-8819-ccd847432aa9/8f6c3fe9-5025-44f6-adcc-a2611a1db139-scaled.jpeg",
    content: `Overvejer du at få monteret solceller på dit hus eller din virksomhed? Solceller kan bruges til at producere egen strøm, men effekten afhænger af korrekt dimensionering og opsætning på taget. Hos JNO Montage arbejder vi som energi montør med salg af solcelleanlæg i Holsted tæt på Vejen samt i Esbjerg og nærliggende områder i Syd- og Vestjylland.

Når vi sælger solceller, tager vi udgangspunkt i bygningens elforbrug, tagets hældning og orientering. Det gælder både helårshus, sommerhus og erhvervsejendomme. En teknisk gennemgang fra starten sikrer, at solcelleanlægget dimensioneres efter ejendommens faktiske elforbrug og tagets muligheder. Solceller kan med fordel kombineres med en luft til vand varmepumpe, hvor egenproduceret strøm anvendes til opvarmning.

Vi håndterer både dimensionering, montering af solceller og den tekniske tilslutning. Vi monterer solceller til både private og erhverv, så du kan få en løsning, der passer til din ejendom.`,
    advantages: [
      "Over 40 års erfaring med energiløsninger og tekniske installationer",
      "Dimensionering af solcelleanlæg baseret på husets eller virksomhedens forbrug",
      "Installation og montering af solceller udført af certificerede montører",
      "Opsætning på både bolig, sommerhus og erhvervsejendom",
      "Samlet tilbud med gennemsigtighed i pris og anlægsstørrelse"
    ],
    steps: [
      {
        step: "Trin 1: Gennemgang af tag og elforbrug",
        desc: "Vi vurderer tagets hældning, orientering og konstruktion samt bygningens elforbrug."
      },
      {
        step: "Trin 2: Dimensionering af solcelleanlæg",
        desc: "Anlæggets størrelse beregnes ud fra forbrug og tagets areal. Det sikrer, at solcellerne passer til ejendommens behov."
      },
      {
        step: "Trin 3: Tilbud med samlet pris",
        desc: "Du modtager et samlet tilbud på solcelleanlæg, montering og tilslutning."
      },
      {
        step: "Trin 4: Montering og opsætning af solceller",
        desc: "Vi udfører installation af solcelleanlæg og sørger for korrekt tilslutning til elinstallationen."
      },
      {
        step: "Trin 5: Teknisk gennemgang og drift",
        desc: "Anlægget gennemgås og testes, så det er klar til produktion."
      }
    ],
    relatedSlugs: ['luft-til-vand-varmepumpe', 'varmepumpe-service', 'montage']
  },

  'varmepumpe-service': {
    title: "Varmepumpe service i Holsted, Vejen og Esbjerg - Få et tilbud",
    metaTitle: "Varmepumpe service i Holsted, Vejen og Esbjerg - Få et tilbud",
    metaDescription: "Leder du efter varmepumpe service i Holsted? +40 års erfaring som certificerede montører i Vejen og Esbjerg. Kontakt os i dag",
    heading: "Varmepumpe service til både private og erhverv",
    subheading: "Samlet løsning med eftersyn, rens og kontrol",
    illustrationKey: "varmepumpe-service",
    image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b7fb5f15-602f-4c3f-8819-ccd847432aa9/f63618d5-325a-4886-a559-868a0757e142.jpeg",
    content: `Har du brug for varmepumpe service, så anlægget bliver gennemgået, renset og kontrolleret? Varmepumpe service handler om at gennemgå, rense og kontrollere anlægget, så det fungerer som det skal i hverdagen. Hos JNO Montage udfører vi som energi montør service på varmepumpe i Holsted tæt på Vejen samt i Esbjerg og nærliggende områder i Syd- og Vestjylland for både private boliger og erhverv.

Vi arbejder med service af varmepumpe på både luft til luft og luft til vand anlæg. Eftersynet omfatter kontrol af centrale komponenter, gennemgang af drift og vurdering af, om anlægget kører som forventet. Med mere end 40 års erfaring med salg, montage og service har vi et solidt grundlag for at vurdere anlæggets drift i både bolig og erhverv.

Når vi udfører varmepumpe service, tager vi udgangspunkt i anlæggets type og alder. Det giver et konkret billede af, hvad der skal justeres eller renses for at opretholde stabil drift.`,
    advantages: [
      "Over 40 års erfaring med varmepumper og energimontage",
      "Service på varmepumpe i både bolig og erhverv",
      "Gennemgang, rens og kontrol af centrale komponenter",
      "Mulighed for fast serviceaftale eller enkelt servicebesøg",
      "Klar pris før service udføres"
    ],
    steps: [
      {
        step: "Trin 1: Gennemgang af drift og indstillinger",
        desc: "Vi starter med at kontrollere, hvordan varmepumpen kører i daglig drift."
      },
      {
        step: "Trin 2: Rensning af filtre og centrale dele",
        desc: "Filtre og udvalgte komponenter renses som en del af service på varmepumpe."
      },
      {
        step: "Trin 3: Kontrol af ude- og indedel",
        desc: "Vi gennemgår både udedel og indedel for at sikre, at anlægget fungerer som det skal."
      },
      {
        step: "Trin 4: Kontrol af tilslutninger og funktion",
        desc: "Tilslutninger og drift gennemgås, så eventuelle afvigelser opdages i tide."
      },
      {
        step: "Trin 5: Afklaring af pris og eventuelle tiltag",
        desc: "Du får besked om anlæggets tilstand og en klar pris, hvis der er behov for yderligere service eller justering."
      }
    ],
    relatedSlugs: ['luft-til-luft-varmepumpe', 'luft-til-vand-varmepumpe', 'solceller']
  },

  'montage': {
    title: "Varmepumpe montage i Esbjerg og Holsted | JNO Energimontør",
    metaTitle: "Varmepumpe montage i Esbjerg og Holsted | JNO Energimontør",
    metaDescription: "Få professionel varmepumpe montage i Esbjerg og Holsted hos JNO Energimontør. Over 40 års erfaring sikrer korrekt installation og optimal drift. Kontakt os nu!",
    heading: "Professionel montage af varmepumper i Esbjerg og Holsted",
    subheading: "Specialister i luft til vand- og luft til luft-varmepumper",
    illustrationKey: "montage",
    image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b7fb5f15-602f-4c3f-8819-ccd847432aa9/8f6c3fe9-5025-44f6-adcc-a2611a1db139-scaled.jpeg",
    content: `Hos JNO Energimontør er vi specialister i montering af varmepumper i Esbjerg og Holsted, særligt luft til vand-varmepumper, der er ideelle til både boliger og virksomheder. Med over 40 års erfaring sikrer vi, at din varmepumpe installeres korrekt første gang, så du opnår optimal ydeevne og energieffektivitet. Vi har fokus på at levere en løsning, der passer til dine behov, og som holder i mange år frem.

En korrekt montering er afgørende for, at din varmepumpe fungerer optimalt. Vores erfarne installatører sikrer en fejlfri proces og overholder alle gældende forskrifter og sikkerhedsstandarder. Vi rådgiver dig om den bedste løsning og sørger for, at din installation tilpasses din bolig og dit energiforbrug. Vælg JNO Energimontør til din næste varmepumpe montering i Esbjerg eller Holsted, og oplev fordelene ved en professionel samarbejdspartner.`,
    advantages: [
      "Over 40 års erfaring med professionel varmepumpemontage",
      "Minimale afbrydelser og hurtig ibrugtagning",
      "Overholdelse af alle gældende forskrifter og sikkerhedsstandarder",
      "Korrekt indregulering for optimal virkningsgrad",
      "Lokal forankring i Holsted, Vejen og Esbjerg"
    ],
    steps: [
      {
        step: "Trin 1: Rådgivning & Placering",
        desc: "Vi finder den optimale placering for udedel og indedel for bedst muligt varmeflow."
      },
      {
        step: "Trin 2: Rørføring & El-tilslutning",
        desc: "Præcis og pæn rørføring udføres af certificerede montører."
      },
      {
        step: "Trin 3: Indregulering & Test",
        desc: "Anlægget indreguleres og funktionstestes grundigt før overdragelse."
      }
    ],
    relatedSlugs: ['salg', 'service', 'luft-til-vand-varmepumpe']
  },

  'salg': {
    title: "Varmepumpe i Esbjerg og Holsted | Få en pris på luft til vand varmepumper",
    metaTitle: "Varmepumpe i Esbjerg og Holsted | Få en pris på luft til vand varmepumper",
    metaDescription: "Køb din luft til vand varmepumpe hos JNO Energimontør i Esbjerg og Holsted. Vi tilbyder konkurrencedygtige varmepumpe priser og professionel rådgivning",
    heading: "Varmepumpe salg i Esbjerg og Holsted – luft til vand-specialister",
    subheading: "Find den rette varmepumpe til dine behov med professionel rådgivning",
    illustrationKey: "salg",
    image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b7fb5f15-602f-4c3f-8819-ccd847432aa9/luft-til-vand.jpg",
    content: `Hos JNO Energimontør er vi eksperter i salg af luft til vand varmepumper, der er en energieffektiv og miljøvenlig løsning til både boliger og virksomheder. Vi tilbyder et bredt udvalg af varmepumper og hjælper dig med at finde den helt rigtige model, der passer til dine specifikke behov. Uanset om du søger en varmepumpe i Esbjerg eller Holsted, er vores erfarne team klar til at rådgive dig om den bedste løsning.

Vi ved, at investering i en varmepumpe er en vigtig beslutning, og derfor tilbyder vi altid løsninger, der kombinerer høj kvalitet med attraktive priser. Når du vælger en varmepumpe luft til vand i Esbjerg eller Holsted, sørger vi for, at du får et produkt, der lever op til dine forventninger og giver betydelige energibesparelser på lang sigt. Contact us for en uforpligtende dialog om varmepumpe priser og muligheder.`,
    advantages: [
      "Stort udvalg af førende mærker og modeller",
      "Ekspertise i dimensionering til det danske klima",
      "Konkurrencedygtige priser på kvalitetsprodukter",
      "Samlet pakke med salg, montering og garanti",
      "Lokal leverandør i Esbjerg og Holsted"
    ],
    steps: [
      {
        step: "Trin 1: Behovsafdækning",
        desc: "Vi gennemgår din bolig og nuværende forbrug for at finde den optimale model."
      },
      {
        step: "Trin 2: Konkret Tilbud",
        desc: "Du modtager et gennemskueligt tilbud uden skjulte ekstraomkostninger."
      },
      {
        step: "Trin 3: Levering & Montering",
        desc: "Anlægget leveres og monteres til aftalt tid af vores montører."
      }
    ],
    relatedSlugs: ['montage', 'service', 'luft-til-vand-varmepumpe']
  },

  'service': {
    title: "Service af varmepumpe i Esbjerg og Holsted | JNO Energimontør",
    metaTitle: "Service af varmepumpe i Esbjerg og Holsted | JNO Energimontør",
    metaDescription: "Få professionel service af varmepumper i Esbjerg og Holsted. JNO Energimontør sikrer optimal ydeevne og drift. Kontakt os for varmepumpe service i dag!",
    heading: "Professionel service af varmepumpe i Esbjerg og Holsted",
    subheading: "Sikring af optimal ydeevne med varmepumpe service",
    illustrationKey: "service",
    image: "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b7fb5f15-602f-4c3f-8819-ccd847432aa9/f63618d5-325a-4886-a559-868a0757e142.jpeg",
    content: `For at sikre, at dit energisystem fungerer optimalt og pålideligt, er regelmæssig service og vedligeholdelse afgørende. Hos JNO Energimontør tilbyder vi professionel service til alle vores produkter.

For at din varmepumpe kan levere optimal ydeevne og lang levetid, er regelmæssig service afgørende. Hos JNO Energimontør tilbyder vi professionel service af varmepumper i Esbjerg og Holsted, hvor vi gennemgår hele systemet for at sikre, at alt fungerer fejlfrit. En grundig varmepumpeservice inkluderer rengøring, fejlsøgning og tjek af systemets vigtigste komponenter, så du undgår uforudsete problemer og dyre reparationer.

Med vores omfattende erfaring inden for varmepumpe service ved vi, hvad der skal til for at holde dit energisystem i topform. Regelmæssig vedligeholdelse er ikke kun en investering i din varmepumpes effektivitet, men også i din egen tryghed.`,
    advantages: [
      "Optimal virkningsgrad og lavere elregning",
      "Forlænget levetid på varmepumpens kompressor og dele",
      "Forebyggelse af akutte nedbrud i kolde måneder",
      "Autoriseret og certificeret eftersyn",
      "Fast og gennemskuelig servicepris"
    ],
    steps: [
      {
        step: "Trin 1: Rengøring & Filtertjek",
        desc: "Rensning af varmeveksler, lameller og luftfiltre for optimal luftstrøm."
      },
      {
        step: "Trin 2: Tryk & Kølemiddeltjek",
        desc: "Måling af driftstryk og kontrol af kølekredsløb for lækager."
      },
      {
        step: "Trin 3: Servicerapport",
        desc: "Gennemgang med kunden og skriftlig bekræftelse på udført service."
      }
    ],
    relatedSlugs: ['varmepumpe-service', 'montage', 'salg']
  }
};

export function ServiceDetailPage() {
  const { slug } = useParams();
  const service = SERVICES_DATA[slug];

  if (!service) {
    return <Navigate to="/404" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <meta property="og:title" content={service.metaTitle} />
        <meta property="og:description" content={service.metaDescription} />
      </Helmet>

      <SubpageHero eyebrow="JNO Montage · Energiløsninger" title={service.heading} description={service.subheading} />

      {/* Body Content */}
      <section className="bg-white py-16 sm:py-20 lg:py-28">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-20">
            
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-10">
              
              {/* Original Scraped Text Paragraphs */}
              <div className="space-y-5 border-y border-[#CBD5E1] py-9 text-sm leading-7 text-[#4B5563] sm:text-base sm:leading-8">
                <h2 className="mb-5 text-3xl font-semibold tracking-tight text-[#0B0E14]">
                  Om {service.heading}
                </h2>
                {service.content.split('\n\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>

              {/* 5 Advantages Box */}
              {service.advantages && (
                <div className="border-b border-[#CBD5E1] pb-10 space-y-5">
                  <h3 className="text-xl font-bold text-[#0B0E14]">
                    Fordele ved at vælge JNO Montage
                  </h3>
                  <div className="space-y-3">
                    {service.advantages.map((adv, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <i className="fa-light fa-check text-[#206FEA] mt-1 shrink-0" aria-hidden="true"></i>
                        <span className="text-sm leading-6 text-slate-600">{adv}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Steps */}
              {service.steps && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-[#0B0E14]">
                    Processen trin for trin
                  </h3>
                  <div className="space-y-4">
                    {service.steps.map((st, i) => (
                      <div key={i} className="border-t border-[#CBD5E1] py-5 space-y-2">
                        <h4 className="font-semibold text-[#0B0E14] text-sm">{st.step}</h4>
                        <p className="text-sm text-slate-600 leading-7">{st.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Right Sidebar Form & Info */}
            <div className="lg:col-span-5 space-y-8">
              <WebForm 
                title={`Få et tilbud på ${service.heading.split(' ')[0]}`}
                subtitle="Udfyld formularen for at blive kontaktet af en certificeret montør."
              />

              {/* Quick Contact Box */}
              <div className="bg-[#0B0E14] text-white p-8 sm:p-10 space-y-4">
                <h3 className="text-2xl font-semibold text-white">
                  Brug for Rådgivning?
                </h3>
                <p className="text-sm text-white/75 leading-7">
                  Har du spørgsmål til valg af anlæg, dimensionering eller tilskudsmuligheder? Ring direkte til montøren:
                </p>
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#206FEA] py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#206FEA]/85 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
